import React, { useState, useEffect, useMemo, useCallback } from "react";
import Select from "react-select";
import {
  Card,
  CardBody,
  Col,
  Container,
  Form,
  Input,
  Label,
  Row,
  Button,
  Table,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  FormGroup,
  Nav,
  NavItem,
  NavLink,
  TabContent,
  TabPane,
} from "reactstrap";
import { 
  getDailyClosingSummary, 
  saveDayClosing, 
  checkDayClosing,
  updateTransactionAmount,
  exportDailyClosingToExcel,
  getDailyClosingOpeningBalance
} from "../../components/Api/GmsDayClosing";
import { getBusinesses } from "../../components/Api/GmsBusiness";
import { getBranches } from "../../components/Api/GmsBranch";
import { Edit, Download } from "lucide-react";
import { ClipLoader } from "react-spinners";
import Swal from "sweetalert2";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { 
  hasPagePermissionWithAdmin, 
  isUserAdmin 
} from "../../components/Permission/PermissionService";
import Breadcrumb from "../../components/Common/Breadcrumb";
import { formatDateForAPI, formatDateForDisplay } from "../../helpers/dateHelper";

// Custom styles for Select components (from PayrollProcessingPage)
const selectStyles = {
  control: (provided, state) => ({
    ...provided,
    minHeight: '38px',
    borderColor: state.isFocused ? '#556ee6' : '#ced4da',
    boxShadow: state.isFocused ? '0 0 0 0.2rem rgba(85, 110, 230, 0.25)' : 'none',
  }),
  menu: (provided) => ({
    ...provided,
    zIndex: 9999,
    maxHeight: 200,
  }),
  menuList: (provided) => ({
    ...provided,
    maxHeight: 180,
    overflowY: 'auto',
  }),
  option: (provided, state) => ({
    ...provided,
    backgroundColor: state.isSelected ? '#556ee6' : state.isFocused ? '#f8f9fa' : 'white',
    color: state.isSelected ? 'white' : '#495057',
    '&:hover': {
      backgroundColor: state.isSelected ? '#556ee6' : '#e9ecef',
    },
  }),
};

// Get user session
const getUserSession = () => {
  const authUser = JSON.parse(localStorage.getItem("authUser") || "{}");
  const user = JSON.parse(localStorage.getItem("user") || "{}");
  return {
    userId: authUser.userId || 1,
    userName: authUser.userName || "System",
    businessId: authUser.businessId || user.user_Business_Id || null,
    branchId: authUser.branchId || user.user_Branch_Id || null,
  };
};

export default function DayClosingPage() {
  const [summary, setSummary] = useState(null); // Complete totals (always all transaction types)
  const [openingBalance, setOpeningBalance] = useState(null);
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [businesses, setBusinesses] = useState([]);
  const [branches, setBranches] = useState([]);
  const [selectedBusiness, setSelectedBusiness] = useState(null);
  const [selectedBranch, setSelectedBranch] = useState(null);
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [activeTab, setActiveTab] = useState("1"); // Default to Cash tab
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [totalCount, setTotalCount] = useState(0);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingTransaction, setEditingTransaction] = useState(null);
  const [editCashAmount, setEditCashAmount] = useState(0);
  const [editCashlessAmount, setEditCashlessAmount] = useState(0);
  const [editNetAmount, setEditNetAmount] = useState(0);
  const [editNotes, setEditNotes] = useState("");
  const [editLoading, setEditLoading] = useState(false);
  const [dayClosingNotes, setDayClosingNotes] = useState("");
  const [holidayNotes, setHolidayNotes] = useState("");
  const [showDayClosingModal, setShowDayClosingModal] = useState(false);
  const [showHolidayModal, setShowHolidayModal] = useState(false);
  const [closingDate, setClosingDate] = useState(new Date());
  const [closingDateError, setClosingDateError] = useState("");

  // Check permissions
  const permissions = JSON.parse(localStorage.getItem('permissions') || '[]');
  const isAdmin = isUserAdmin();
  const hasPermission = hasPagePermissionWithAdmin(permissions, '/GmsDailyClosing');

  // Get user session
  const session = useMemo(() => getUserSession(), []);
  const userBusinessId = session.businessId;
  const userBranchId = session.branchId;

  // Using centralized date helper from helpers/dateHelper.js

  // Load businesses
  const loadBusinesses = useCallback(async () => {
    try {
      const data = await getBusinesses({});
      const businessData = Array.isArray(data) ? data : (data.data || []);
      setBusinesses(businessData);
      
      // If user has business, auto-select it
      if (userBusinessId && !selectedBusiness) {
        const userBusiness = businessData.find(b => b.business_Id === userBusinessId);
        if (userBusiness) {
          setSelectedBusiness({
            value: userBusinessId,
            label: userBusiness.business_NameEn || userBusiness.business_Name
          });
        }
      }
    } catch (error) {
      console.error("Error loading businesses:", error);
      Swal.fire("Error", "Failed to load businesses", "error");
    }
  }, [userBusinessId, selectedBusiness]);

  // Load branches based on selected business
  const loadBranches = useCallback(async (businessId) => {
    if (!businessId) {
      setBranches([]);
      return;
    }
    try {
      const response = await getBranches({ Business_Id: businessId });
      const branchData = Array.isArray(response) ? response : (response.data || []);
      setBranches(branchData);
      
      // If user has branch, auto-select it
      if (userBranchId && !selectedBranch) {
        const userBranch = branchData.find(b => b.branch_Id === userBranchId);
        if (userBranch) {
          setSelectedBranch({
            value: userBranchId,
            label: `${userBranch.branch_NameEn} - ${userBranch.branch_NameAr}`
          });
        }
      }
    } catch (error) {
      console.error("Error loading branches:", error);
      Swal.fire("Error", "Failed to load branches", "error");
    }
  }, [userBranchId, selectedBranch]);

  // Get transaction type from active tab
  const getTransactionType = useCallback(() => {
    const type = (() => {
      switch (activeTab) {
        case "1": return "CASH";
        case "2": return "CASHLESS";
        case "3": return "CREDIT";
        case "4": return "RETURN";
        case "5": return null; // All
        default: return "CASH";
      }
    })();
    console.log("🔍 [DayClosing] getTransactionType - activeTab:", activeTab, "-> transactionType:", type);
    return type;
  }, [activeTab]);

  // Load opening balance
  const loadOpeningBalance = useCallback(async () => {
    if (!selectedBusiness || !selectedBranch) {
      setOpeningBalance({
        cashTotal: 0,
        cashlessTotal: 0,
        creditTotal: 0,
        returnsTotal: 0,
        netTotal: 0,
        closingDate: null
      });
      return;
    }

    try {
      const formattedDate = formatDateForAPI(selectedDate);
      const filter = {
        closingDate: formattedDate,
        businessId: selectedBusiness.value,
        branchId: selectedBranch.value
      };

      const response = await getDailyClosingOpeningBalance(filter);
      
      setOpeningBalance(response || {
        cashTotal: 0,
        cashlessTotal: 0,
        creditTotal: 0,
        returnsTotal: 0,
        netTotal: 0,
        closingDate: null
      });
    } catch (error) {
      console.error("Error loading opening balance:", error);
      setOpeningBalance({
        cashTotal: 0,
        cashlessTotal: 0,
        creditTotal: 0,
        returnsTotal: 0,
        netTotal: 0,
        closingDate: null
      });
    }
  }, [selectedDate, selectedBusiness, selectedBranch]);

  // Load complete summary (all transaction types) for totals display
  const loadCompleteSummary = useCallback(async () => {
    if (!selectedBusiness || !selectedBranch) {
      setSummary({
        cashTotal: 0,
        cashlessTotal: 0,
        creditTotal: 0,
        returnsTotal: 0,
        netTotal: 0
      });
      return;
    }

    const formattedDate = formatDateForAPI(selectedDate);

    try {
      const filter = {
        closingDate: formattedDate,
        businessId: selectedBusiness.value,
        branchId: selectedBranch.value,
        transactionType: null, // Always get complete totals
        page: 1,
        pageSize: 1 // We only need totals, not transactions
      };
      
      const response = await getDailyClosingSummary(filter);
      
      if (response) {
        setSummary({
          cashTotal: response.cashTotal || 0,
          cashlessTotal: response.cashlessTotal || 0,
          creditTotal: response.creditTotal || 0,
          returnsTotal: response.returnsTotal || 0,
          netTotal: response.netTotal || 0
        });
      } else {
        setSummary({
          cashTotal: 0,
          cashlessTotal: 0,
          creditTotal: 0,
          returnsTotal: 0,
          netTotal: 0
        });
      }
    } catch (error) {
      console.error("Error loading complete summary:", error);
      setSummary({
        cashTotal: 0,
        cashlessTotal: 0,
        creditTotal: 0,
        returnsTotal: 0,
        netTotal: 0
      });
    }
  }, [selectedDate, selectedBusiness, selectedBranch]);

  // Load daily closing transactions (filtered by tab)
  const loadDailyClosingData = useCallback(async () => {
    if (!selectedBusiness || !selectedBranch) {
      setTransactions([]);
      setTotalCount(0);
      return;
    }

    const transactionType = getTransactionType();
    const formattedDate = formatDateForAPI(selectedDate);

    setLoading(true);
    try {
      const filter = {
        closingDate: formattedDate,
        businessId: selectedBusiness.value,
        branchId: selectedBranch.value,
        transactionType: transactionType,
        page: page,
        pageSize: pageSize
      };
      
      const response = await getDailyClosingSummary(filter);
      
      if (response) {
        const transactionsList = response.transactions || [];
        setTransactions(transactionsList);
        setTotalCount(transactionsList.length);
      } else {
        setTransactions([]);
        setTotalCount(0);
      }
    } catch (error) {
      console.error("Error loading daily closing data:", error);
      Swal.fire("Error", "Failed to load daily closing data: " + (error.response?.data?.message || error.message), "error");
      setTransactions([]);
      setTotalCount(0);
    } finally {
      setLoading(false);
    }
  }, [selectedDate, selectedBusiness, selectedBranch, page, pageSize, activeTab, getTransactionType]);

  // Initial load
  useEffect(() => {
    if (hasPermission) {
      loadBusinesses();
    } else {
      window.location.href = '/Dashboard';
    }
  }, [hasPermission, loadBusinesses]);

  // Load branches when business changes
  useEffect(() => {
    if (selectedBusiness) {
      loadBranches(selectedBusiness.value);
    } else {
      setBranches([]);
      setSelectedBranch(null);
    }
  }, [selectedBusiness, loadBranches]);

  // Load data when filters change (only when branch is selected)
  useEffect(() => {
    if (selectedBusiness && selectedBranch && hasPermission) {
      loadOpeningBalance();
      loadCompleteSummary();
      loadDailyClosingData();
    } else {
      if (!selectedBranch) {
        setSummary({
          cashTotal: 0,
          cashlessTotal: 0,
          creditTotal: 0,
          returnsTotal: 0,
          netTotal: 0
        });
        setOpeningBalance({
          cashTotal: 0,
          cashlessTotal: 0,
          creditTotal: 0,
          returnsTotal: 0,
          netTotal: 0,
          closingDate: null
        });
        setTransactions([]);
        setTotalCount(0);
      }
    }
  }, [selectedDate, selectedBusiness, selectedBranch, page, pageSize, activeTab, loadDailyClosingData, loadCompleteSummary, loadOpeningBalance, hasPermission]);

  // Business options
  const businessOptions = useMemo(() => {
    return businesses.map(b => ({
      value: b.business_Id,
      label: b.business_NameEn || b.business_Name
    }));
  }, [businesses]);

  // Branch options
  const branchOptions = useMemo(() => {
    return branches.map(b => ({
      value: b.branch_Id,
      label: `${b.branch_NameEn} - ${b.branch_NameAr}`
    }));
  }, [branches]);

  // Get paginated transactions
  const getPaginatedTransactions = () => {
    const startIndex = (page - 1) * pageSize;
    const endIndex = startIndex + pageSize;
    return transactions.slice(startIndex, endIndex);
  };

  // Handle page change
  const handlePageChange = (newPage) => {
    setPage(newPage);
  };

  // Handle edit transaction
  const handleEditTransaction = (transaction) => {
    setEditingTransaction(transaction);
    setEditCashAmount(transaction.cashAmount || 0);
    setEditCashlessAmount(transaction.cashlessAmount || 0);
    setEditNetAmount(transaction.netAmount || 0);
    setEditNotes("");
    setShowEditModal(true);
  };

  // Handle save edited transaction
  const handleSaveEdit = async () => {
    // Validate: Cash + Cashless must equal Net
    if (editCashAmount + editCashlessAmount !== editNetAmount) {
      Swal.fire("Error", `Cash amount + Bank amount must equal Net amount (${editNetAmount.toFixed(2)})`, "error");
      return;
    }

    setEditLoading(true);
    try {
      const request = {
        transactionId: editingTransaction.transactionId,
        tableType: editingTransaction.tableType,
        cashAmount: editCashAmount,
        cashlessAmount: editCashlessAmount,
        notes: editNotes || null
      };

      const response = await updateTransactionAmount(request);
      
      if (response.message) {
        Swal.fire("Success", response.message, "success");
        setShowEditModal(false);
        loadDailyClosingData(); // Reload data
      } else {
        Swal.fire("Error", "Failed to update transaction amount", "error");
      }
    } catch (error) {
      console.error("Error updating transaction:", error);
      Swal.fire("Error", error.response?.data?.message || "Failed to update transaction amount", "error");
    } finally {
      setEditLoading(false);
    }
  };

  // Handle mark day closing
  const handleMarkDayClosing = () => {
    // Validate branch is selected
    if (!selectedBranch) {
      Swal.fire("Validation Error", "Please select a branch before marking day closing.", "error");
      return;
    }

    // Reset closing date to current date and open modal
    setClosingDate(new Date());
    setClosingDateError("");
    setDayClosingNotes("");
    setShowDayClosingModal(true);
  };

  // Validate and save day closing
  const handleSaveDayClosing = async () => {
    // Validate date
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const selectedClosingDate = new Date(closingDate);
    selectedClosingDate.setHours(0, 0, 0, 0);
    
    // Check if future date
    if (selectedClosingDate > today) {
      setClosingDateError("Cannot select future dates. Please select today or a previous date.");
      return;
    }

    // Check if date already exists
    try {
      const checkRequest = {
        transactionDate: formatDateForAPI(closingDate),
        businessId: selectedBusiness.value,
        branchId: selectedBranch.value
      };
      
      const dayClosingCheck = await checkDayClosing(checkRequest);
      
      if (dayClosingCheck.isClosed) {
        setClosingDateError("Day closing or holiday already exists for this date. Please select a different date.");
        return;
      }
    } catch (error) {
      console.error("Error checking day closing:", error);
      // Continue with save if check fails
    }

    setClosingDateError("");
    setLoading(true);
    
    try {
      const request = {
        closingDate: formatDateForAPI(closingDate),
        businessId: selectedBusiness.value,
        branchId: selectedBranch.value,
        holiday: false,
        notes: dayClosingNotes || null
      };

      const response = await saveDayClosing(request);
      
      if (response.message) {
        Swal.fire("Success", response.message, "success");
        setShowDayClosingModal(false);
        setDayClosingNotes("");
        loadDailyClosingData(); // Reload data
      } else {
        Swal.fire("Error", "Failed to mark day closing", "error");
      }
    } catch (error) {
      console.error("Error marking day closing:", error);
      const errorMessage = error.response?.data?.message || "Failed to mark day closing";
      if (errorMessage.includes("already") || errorMessage.includes("duplicate") || errorMessage.includes("unique")) {
        setClosingDateError("Day closing or holiday already exists for this date. Please select a different date.");
      } else {
        Swal.fire("Error", errorMessage, "error");
      }
    } finally {
      setLoading(false);
    }
  };

  // Handle mark holiday
  const handleMarkHoliday = () => {
    // Validate branch is selected
    if (!selectedBranch) {
      Swal.fire("Validation Error", "Please select a branch before marking as holiday.", "error");
      return;
    }

    // Reset closing date to current date and open modal
    setClosingDate(new Date());
    setClosingDateError("");
    setHolidayNotes("");
    setShowHolidayModal(true);
  };

  // Validate and save holiday
  const handleSaveHoliday = async () => {
    // Validate date
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const selectedClosingDate = new Date(closingDate);
    selectedClosingDate.setHours(0, 0, 0, 0);
    
    // Check if future date
    if (selectedClosingDate > today) {
      setClosingDateError("Cannot select future dates. Please select today or a previous date.");
      return;
    }

    // Check if date already exists
    try {
      const checkRequest = {
        transactionDate: formatDateForAPI(closingDate),
        businessId: selectedBusiness.value,
        branchId: selectedBranch.value
      };
      
      const dayClosingCheck = await checkDayClosing(checkRequest);
      
      if (dayClosingCheck.isClosed) {
        setClosingDateError("Day closing or holiday already exists for this date. Please select a different date.");
        return;
      }
    } catch (error) {
      console.error("Error checking day closing:", error);
      // Continue with save if check fails
    }

    setClosingDateError("");
    setLoading(true);
    
    try {
      const request = {
        closingDate: formatDateForAPI(closingDate),
        businessId: selectedBusiness.value,
        branchId: selectedBranch.value,
        holiday: true,
        notes: holidayNotes || null
      };

      const response = await saveDayClosing(request);
      
      if (response.message) {
        Swal.fire("Success", response.message, "success");
        setShowHolidayModal(false);
        setHolidayNotes("");
        loadDailyClosingData(); // Reload data
      } else {
        Swal.fire("Error", "Failed to mark holiday", "error");
      }
    } catch (error) {
      console.error("Error marking holiday:", error);
      const errorMessage = error.response?.data?.message || "Failed to mark holiday";
      if (errorMessage.includes("already") || errorMessage.includes("duplicate") || errorMessage.includes("unique")) {
        setClosingDateError("Day closing or holiday already exists for this date. Please select a different date.");
      } else {
        Swal.fire("Error", errorMessage, "error");
      }
    } finally {
      setLoading(false);
    }
  };

  // Handle export to Excel (tab-specific)
  const handleExportToExcel = async () => {
    try {
      setLoading(true);
      const transactionType = getTransactionType();
      const filter = {
        closingDate: formatDateForAPI(selectedDate),
        businessId: selectedBusiness.value,
        branchId: selectedBranch?.value || null,
        transactionType: transactionType
      };

      await exportDailyClosingToExcel(filter);
      Swal.fire("Success", "Daily closing summary exported successfully", "success");
    } catch (error) {
      console.error("Error exporting:", error);
      Swal.fire("Error", "Failed to export daily closing summary", "error");
    } finally {
      setLoading(false);
    }
  };

  // Calculate closing balance (Opening + Current)
  const getClosingBalance = useMemo(() => {
    if (!openingBalance || !summary) {
      return {
        cashTotal: 0,
        cashlessTotal: 0,
        creditTotal: 0,
        returnsTotal: 0,
        netTotal: 0
      };
    }
    return {
      cashTotal: (openingBalance.cashTotal || 0) + (summary.cashTotal || 0),
      cashlessTotal: (openingBalance.cashlessTotal || 0) + (summary.cashlessTotal || 0),
      creditTotal: (openingBalance.creditTotal || 0) + (summary.creditTotal || 0),
      returnsTotal: (openingBalance.returnsTotal || 0) + (summary.returnsTotal || 0),
      netTotal: (openingBalance.netTotal || 0) + (summary.netTotal || 0)
    };
  }, [openingBalance, summary]);

  // Check if has transactions (for conditional button display)
  const hasTransactions = useMemo(() => {
    if (!summary) return false;
    return (
      (summary.cashTotal !== 0 && summary.cashTotal !== null) ||
      (summary.cashlessTotal !== 0 && summary.cashlessTotal !== null) ||
      (summary.creditTotal !== 0 && summary.creditTotal !== null) ||
      (summary.returnsTotal !== 0 && summary.returnsTotal !== null) ||
      (transactions && transactions.length > 0)
    );
  }, [summary, transactions]);

  // Format currency
  const formatCurrency = (amount) => {
    if (amount === null || amount === undefined) return '-';
    return new Intl.NumberFormat('en-US', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(amount);
  };

  // Format date
  const formatDate = (dateString) => {
    try {
      const date = new Date(dateString);
      const day = date.getDate().toString().padStart(2, '0');
      const month = (date.getMonth() + 1).toString().padStart(2, '0');
      const year = date.getFullYear();
      return `${day}/${month}/${year}`;
    } catch (error) {
      return "N/A";
    }
  };

  // Format time
  const formatTime = (dateString) => {
    try {
      const date = new Date(dateString);
      const hours = date.getHours().toString().padStart(2, '0');
      const minutes = date.getMinutes().toString().padStart(2, '0');
      const seconds = date.getSeconds().toString().padStart(2, '0');
      return `${hours}:${minutes}:${seconds}`;
    } catch (error) {
      return "N/A";
    }
  };

  // Get transaction type label
  const getTransactionTypeLabel = (tableType) => {
    switch (tableType) {
      case 'SAL': return 'Sales Invoice';
      case 'SRT': return 'Sales Return';
      case 'REC': return 'Receipt';
      case 'ADV': return 'Advance Receipt';
      case 'CDP': return 'Cash Deposit';
      default: return tableType;
    }
  };

  if (!hasPermission) {
    return null;
  }

  const paginatedTransactions = getPaginatedTransactions();

  return (
    <div className="page-content">
      <Container fluid>
        <Breadcrumb title="X Report" breadcrumbItem="POS & Billing" />
        
        <Row>
          <Col lg="12">
            <Card>
              <CardBody>
                <h4 className="card-title mb-3">X Report</h4>

                {/* Filters */}
                <Card className="mb-4">
                  <CardBody>
                    <Row>
                      <Col md="3">
                        <Label>Business</Label>
                        <Select
                          options={businessOptions}
                          value={selectedBusiness}
                          onChange={(option) => {
                            setSelectedBusiness(option);
                            setSelectedBranch(null);
                            setPage(1);
                          }}
                          isClearable={!userBusinessId}
                          isDisabled={!!userBusinessId}
                          isSearchable
                          placeholder="Select Business"
                          styles={selectStyles}
                          maxMenuHeight={200}
                          menuPlacement="auto"
                        />
                      </Col>
                      <Col md="3">
                        <Label>Branch</Label>
                        <Select
                          options={branchOptions}
                          value={selectedBranch}
                          onChange={(option) => {
                            setSelectedBranch(option);
                            setPage(1);
                          }}
                          isClearable={!userBranchId}
                          isDisabled={!selectedBusiness || !!userBranchId}
                          isSearchable
                          placeholder="Select Branch"
                          styles={selectStyles}
                          maxMenuHeight={200}
                          menuPlacement="auto"
                        />
                      </Col>
                      <Col md="3">
                        <Label>Date</Label>
                        <DatePicker
                          selected={selectedDate}
                          onChange={(date) => {
                            setSelectedDate(date);
                            setPage(1);
                          }}
                          dateFormat="dd/MM/yyyy"
                          className="form-control"
                          maxDate={new Date()}
                        />
                      </Col>
                      <Col md="3" className="d-flex align-items-end">
                        {hasTransactions ? (
                          <Button
                            color="primary"
                            size="sm"
                            onClick={handleMarkDayClosing}
                            disabled={loading || !selectedBusiness || !selectedBranch}
                            style={{ height: '38px' }}
                          >
                            Mark Day Closing
                          </Button>
                        ) : (
                          <Button
                            color="warning"
                            size="sm"
                            onClick={handleMarkHoliday}
                            disabled={loading || !selectedBusiness || !selectedBranch}
                            style={{ height: '38px' }}
                          >
                            Mark as Holiday
                          </Button>
                        )}
                      </Col>
                    </Row>
                  </CardBody>
                </Card>

                {/* Show message if business or branch not selected */}
                {(!selectedBusiness || !selectedBranch) && (
                  <Card className="mb-4" style={{ border: '2px solid #ffc107' }}>
                    <CardBody className="text-center py-4">
                      <i className="mdi mdi-information-outline" style={{ fontSize: '48px', color: '#ffc107', marginBottom: '15px' }}></i>
                      <h5 style={{ color: '#856404', marginBottom: '10px' }}>Please Select Business and Branch</h5>
                      <p className="text-muted mb-0" style={{ fontSize: '14px' }}>
                        {!selectedBusiness && !selectedBranch 
                          ? "Please select a Business and Branch from the dropdowns above to view the X Report."
                          : !selectedBusiness 
                          ? "Please select a Business from the dropdown above to view the X Report."
                          : "Please select a Branch from the dropdown above to view the X Report."}
                      </p>
                    </CardBody>
                  </Card>
                )}

                {/* Opening Balance - Above Tabs */}
                {selectedBusiness && selectedBranch && openingBalance && (
                  <Card className="mb-3" style={{ border: '2px solid #28a745' }}>
                    <CardBody style={{ padding: '0.5rem' }}>
                      <h6 className="mb-1" style={{ color: '#28a745', fontWeight: 'bold', fontSize: '13px' }}>
                        Opening Balance (Up to Yesterday)
                      </h6>
                      <Row>
                        <Col md="2">
                          <div className="text-center p-1" style={{ backgroundColor: '#f8f9fa', borderRadius: '0.375rem' }}>
                            <Label className="text-muted mb-0" style={{ fontSize: '11px' }}>Cash Total</Label>
                            <h6 className="mb-0" style={{ fontSize: '14px', fontWeight: '600' }}>{formatCurrency(openingBalance.cashTotal)}</h6>
                          </div>
                        </Col>
                        <Col md="2">
                          <div className="text-center p-1" style={{ backgroundColor: '#f8f9fa', borderRadius: '0.375rem' }}>
                            <Label className="text-muted mb-0" style={{ fontSize: '11px' }}>Bank Total</Label>
                            <h6 className="mb-0" style={{ fontSize: '14px', fontWeight: '600' }}>{formatCurrency(openingBalance.cashlessTotal)}</h6>
                          </div>
                        </Col>
                        <Col md="2">
                          <div className="text-center p-1" style={{ backgroundColor: '#f8f9fa', borderRadius: '0.375rem' }}>
                            <Label className="text-muted mb-0" style={{ fontSize: '11px' }}>Credit Total</Label>
                            <h6 className="mb-0" style={{ fontSize: '14px', fontWeight: '600' }}>{formatCurrency(openingBalance.creditTotal)}</h6>
                          </div>
                        </Col>
                        <Col md="2">
                          <div className="text-center p-1" style={{ backgroundColor: '#f8f9fa', borderRadius: '0.375rem' }}>
                            <Label className="text-muted mb-0" style={{ fontSize: '11px' }}>Returns Total</Label>
                            <h6 className="mb-0" style={{ fontSize: '14px', fontWeight: '600' }}>{formatCurrency(openingBalance.returnsTotal)}</h6>
                          </div>
                        </Col>
                        <Col md="4">
                          <div className="text-center p-1" style={{ backgroundColor: '#28a745', borderRadius: '0.375rem', color: 'white' }}>
                            <Label className="mb-0" style={{ color: 'white', fontSize: '11px' }}>Net Total</Label>
                            <h6 className="mb-0" style={{ color: 'white', fontSize: '14px', fontWeight: '600' }}>
                              {formatCurrency(openingBalance.netTotal)}
                            </h6>
                          </div>
                        </Col>
                      </Row>
                    </CardBody>
                  </Card>
                )}

                {/* Tabs Navigation */}
                {selectedBusiness && selectedBranch && (
                  <Card className="mb-4">
                    <CardBody className="p-0">
                      <div className="d-flex justify-content-between align-items-center" style={{ borderBottom: '1px solid #dee2e6' }}>
                        <Nav tabs className="nav-tabs-custom" style={{ flex: 1 }}>
                      <NavItem>
                        <NavLink
                          style={{ 
                            cursor: "pointer",
                            fontWeight: activeTab === "1" ? "bold" : "normal",
                            color: activeTab === "1" ? "#556ee6" : "#74788d",
                            borderBottom: activeTab === "1" ? "2px solid #556ee6" : "none",
                            padding: "15px 25px",
                            fontSize: "16px"
                          }}
                          className={activeTab === "1" ? "active" : ""}
                          onClick={() => {
                            setActiveTab("1");
                            setPage(1);
                          }}
                        >
                          Cash
                        </NavLink>
                      </NavItem>
                      <NavItem>
                        <NavLink
                          style={{ 
                            cursor: "pointer",
                            fontWeight: activeTab === "2" ? "bold" : "normal",
                            color: activeTab === "2" ? "#556ee6" : "#74788d",
                            borderBottom: activeTab === "2" ? "2px solid #556ee6" : "none",
                            padding: "15px 25px",
                            fontSize: "16px"
                          }}
                          className={activeTab === "2" ? "active" : ""}
                          onClick={() => {
                            setActiveTab("2");
                            setPage(1);
                          }}
                        >
                          Bank
                        </NavLink>
                      </NavItem>
                      <NavItem>
                        <NavLink
                          style={{ 
                            cursor: "pointer",
                            fontWeight: activeTab === "3" ? "bold" : "normal",
                            color: activeTab === "3" ? "#556ee6" : "#74788d",
                            borderBottom: activeTab === "3" ? "2px solid #556ee6" : "none",
                            padding: "15px 25px",
                            fontSize: "16px"
                          }}
                          className={activeTab === "3" ? "active" : ""}
                          onClick={() => {
                            setActiveTab("3");
                            setPage(1);
                          }}
                        >
                          Credit
                        </NavLink>
                      </NavItem>
                      <NavItem>
                        <NavLink
                          style={{ 
                            cursor: "pointer",
                            fontWeight: activeTab === "4" ? "bold" : "normal",
                            color: activeTab === "4" ? "#556ee6" : "#74788d",
                            borderBottom: activeTab === "4" ? "2px solid #556ee6" : "none",
                            padding: "15px 25px",
                            fontSize: "16px"
                          }}
                          className={activeTab === "4" ? "active" : ""}
                          onClick={() => {
                            setActiveTab("4");
                            setPage(1);
                          }}
                        >
                          Sales Return
                        </NavLink>
                      </NavItem>
                      <NavItem>
                        <NavLink
                          style={{ 
                            cursor: "pointer",
                            fontWeight: activeTab === "5" ? "bold" : "normal",
                            color: activeTab === "5" ? "#556ee6" : "#74788d",
                            borderBottom: activeTab === "5" ? "2px solid #556ee6" : "none",
                            padding: "15px 25px",
                            fontSize: "16px"
                          }}
                          className={activeTab === "5" ? "active" : ""}
                          onClick={() => {
                            setActiveTab("5");
                            setPage(1);
                          }}
                        >
                          All
                        </NavLink>
                      </NavItem>
                    </Nav>
                        <div className="d-flex align-items-center" style={{ paddingRight: '15px' }}>
                          <Button
                            color="success"
                            size="sm"
                            onClick={handleExportToExcel}
                            disabled={loading || !selectedBusiness || !selectedBranch}
                            style={{ height: '38px', fontSize: '12px', padding: '0 12px' }}
                          >
                            <Download className="me-1" size={14} />
                            Export to Excel
                          </Button>
                        </div>
                      </div>
                    
                    <TabContent activeTab={activeTab} className="p-4">

                      {/* Transactions Table */}
                      {selectedBusiness && selectedBranch && (
                        <TabPane tabId={activeTab}>
                        <Card>
                          <CardBody>
                            <h5 className="card-title mb-3">
                              Transactions ({totalCount} records)
                            </h5>
                            <div className="table-responsive">
                              {loading ? (
                                <div className="text-center py-4">
                                  <ClipLoader color="#556ee6" loading={loading} size={35} />
                                  <p className="mt-2">Loading transactions...</p>
                                </div>
                              ) : (
                                <>
                                  <Table bordered>
                                    <thead className="table-light">
                                      <tr>
                                        <th>Sr.No</th>
                                        <th>Transaction Number</th>
                                        <th>Transaction Type</th>
                                        {(activeTab === "1" || activeTab === "4" || activeTab === "5") && <th>Cash Amount</th>}
                                        {(activeTab === "2" || activeTab === "4" || activeTab === "5") && <th>Bank Amount</th>}
                                        {(activeTab === "3" || activeTab === "5") && <th>Credit Amount</th>}
                                        <th>Date</th>
                                        <th>Time</th>
                                        <th>Actions</th>
                                      </tr>
                                    </thead>
                                    <tbody>
                                      {paginatedTransactions.length === 0 ? (
                                        <tr>
                                          <td colSpan={
                                            3 + // Sr.No, Transaction Number, Transaction Type
                                            ((activeTab === "1" || activeTab === "4" || activeTab === "5") ? 1 : 0) + // Cash
                                            ((activeTab === "2" || activeTab === "4" || activeTab === "5") ? 1 : 0) + // Bank
                                            ((activeTab === "3" || activeTab === "5") ? 1 : 0) + // Credit
                                            2 + // Date, Time
                                            1 // Actions
                                          } className="text-center">
                                            No transactions found
                                          </td>
                                        </tr>
                                      ) : (
                                        paginatedTransactions.map((transaction, index) => (
                                          <tr key={transaction.transactionId}>
                                            <td>{(page - 1) * pageSize + index + 1}</td>
                                            <td>{transaction.transactionNumber}</td>
                                            <td>{getTransactionTypeLabel(transaction.tableType)}</td>
                                            {(activeTab === "1" || activeTab === "4" || activeTab === "5") && (
                                              <td style={{ color: transaction.cashAmount < 0 ? '#dc3545' : 'inherit' }}>
                                                {transaction.cashAmount < 0 ? '-' : ''}{formatCurrency(Math.abs(transaction.cashAmount))}
                                              </td>
                                            )}
                                            {(activeTab === "2" || activeTab === "4" || activeTab === "5") && (
                                              <td>{formatCurrency(transaction.cashlessAmount)}</td>
                                            )}
                                            {(activeTab === "3" || activeTab === "5") && (
                                              <td>{formatCurrency(transaction.creditAmount)}</td>
                                            )}
                                            <td>{formatDate(transaction.transactionDate)}</td>
                                            <td>{formatTime(transaction.transactionDateTime)}</td>
                                            <td>
                                              {/* Only show edit button for sales invoices (SAL) that are NOT credit transactions */}
                                              {transaction.tableType === 'SAL' && 
                                               transaction.creditAmount === 0 && 
                                               (transaction.cashAmount > 0 || transaction.cashlessAmount > 0) ? (
                                                <Button
                                                  color="info"
                                                  size="sm"
                                                  onClick={() => handleEditTransaction(transaction)}
                                                >
                                                  <Edit size={14} className="me-1" />
                                                  Edit
                                                </Button>
                                              ) : (
                                                <span className="text-muted" style={{ fontSize: '12px' }}>-</span>
                                              )}
                                            </td>
                                          </tr>
                                        ))
                                      )}
                                    </tbody>
                                  </Table>

                                  {/* Pagination */}
                                  {totalCount > pageSize && (
                                    <div className="d-flex justify-content-between align-items-center mt-4 p-3" 
                                         style={{backgroundColor: '#f8f9fa', borderRadius: '0.375rem', border: '1px solid #dee2e6'}}>
                                      <div className="text-muted" style={{fontSize: '14px', fontWeight: '500'}}>
                                        <i className="mdi mdi-information-outline me-2" style={{color: '#556ee6'}}></i>
                                        Showing <span style={{color: '#556ee6', fontWeight: '600'}}>{totalCount > 0 ? (page - 1) * pageSize + 1 : 0}</span> to <span style={{color: '#556ee6', fontWeight: '600'}}>{Math.min(page * pageSize, totalCount)}</span> of <span style={{color: '#556ee6', fontWeight: '600'}}>{totalCount}</span> entries
                                      </div>
                                      <div style={{display: 'flex', alignItems: 'center', gap: '8px'}}>
                                        <Button
                                          color="light"
                                          size="sm"
                                          disabled={page === 1}
                                          onClick={() => handlePageChange(page - 1)}
                                          style={{
                                            border: '1px solid #dee2e6',
                                            borderRadius: '0.375rem',
                                            minWidth: '32px',
                                            height: '32px',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            backgroundColor: page === 1 ? '#f8f9fa' : '#fff',
                                            color: page === 1 ? '#6c757d' : '#495057'
                                          }}
                                        >
                                          <i className="mdi mdi-chevron-left"></i>
                                        </Button>
                                        
                                        {[...Array(Math.ceil(totalCount / pageSize))].map((_, index) => {
                                          const pageNum = index + 1;
                                          const isActive = page === pageNum;
                                          
                                          return (
                                            <Button
                                              key={pageNum}
                                              color={isActive ? "primary" : "light"}
                                              size="sm"
                                              onClick={() => handlePageChange(pageNum)}
                                              style={{
                                                border: '1px solid #dee2e6',
                                                borderRadius: '0.375rem',
                                                minWidth: '32px',
                                                height: '32px',
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                                backgroundColor: isActive ? '#556ee6' : '#fff',
                                                borderColor: isActive ? '#556ee6' : '#dee2e6',
                                                color: isActive ? '#fff' : '#495057',
                                                fontWeight: isActive ? '600' : '400',
                                                boxShadow: isActive ? '0 2px 4px rgba(85,110,230,0.25)' : 'none'
                                              }}
                                            >
                                              {pageNum}
                                            </Button>
                                          );
                                        })}
                                        
                                        <Button
                                          color="light"
                                          size="sm"
                                          disabled={page === Math.ceil(totalCount / pageSize)}
                                          onClick={() => handlePageChange(page + 1)}
                                          style={{
                                            border: '1px solid #dee2e6',
                                            borderRadius: '0.375rem',
                                            minWidth: '32px',
                                            height: '32px',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            backgroundColor: page === Math.ceil(totalCount / pageSize) ? '#f8f9fa' : '#fff',
                                            color: page === Math.ceil(totalCount / pageSize) ? '#6c757d' : '#495057'
                                          }}
                                        >
                                          <i className="mdi mdi-chevron-right"></i>
                                        </Button>
                                      </div>
                                    </div>
                                  )}
                                </>
                              )}
                            </div>
                          </CardBody>
                        </Card>
                      </TabPane>
                    )}
                    </TabContent>
                    </CardBody>
                  </Card>
                )}

                {/* Current Day Totals */}
                {selectedBusiness && selectedBranch && summary && (
                  <Card className="mb-3" style={{ border: '2px solid #556ee6' }}>
                    <CardBody style={{ padding: '0.5rem' }}>
                      <h6 className="mb-1" style={{ color: '#556ee6', fontWeight: 'bold', fontSize: '13px' }}>
                        Current Day Totals ({formatDateForDisplay(selectedDate, 'date')})
                      </h6>
                      <Row>
                        <Col md="2">
                          <div className="text-center p-1" style={{ backgroundColor: '#f8f9fa', borderRadius: '0.375rem' }}>
                            <Label className="text-muted mb-0" style={{ fontSize: '11px' }}>Cash Total</Label>
                            <h6 className="mb-0" style={{ fontSize: '14px', fontWeight: '600' }}>{formatCurrency(summary.cashTotal)}</h6>
                          </div>
                        </Col>
                        <Col md="2">
                          <div className="text-center p-1" style={{ backgroundColor: '#f8f9fa', borderRadius: '0.375rem' }}>
                            <Label className="text-muted mb-0" style={{ fontSize: '11px' }}>Bank Total</Label>
                            <h6 className="mb-0" style={{ fontSize: '14px', fontWeight: '600' }}>{formatCurrency(summary.cashlessTotal)}</h6>
                          </div>
                        </Col>
                        <Col md="2">
                          <div className="text-center p-1" style={{ backgroundColor: '#f8f9fa', borderRadius: '0.375rem' }}>
                            <Label className="text-muted mb-0" style={{ fontSize: '11px' }}>Credit Total</Label>
                            <h6 className="mb-0" style={{ fontSize: '14px', fontWeight: '600' }}>{formatCurrency(summary.creditTotal)}</h6>
                          </div>
                        </Col>
                        <Col md="2">
                          <div className="text-center p-1" style={{ backgroundColor: '#f8f9fa', borderRadius: '0.375rem' }}>
                            <Label className="text-muted mb-0" style={{ fontSize: '11px' }}>Returns Total</Label>
                            <h6 className="mb-0" style={{ fontSize: '14px', fontWeight: '600' }}>{formatCurrency(summary.returnsTotal)}</h6>
                          </div>
                        </Col>
                        <Col md="4">
                          <div className="text-center p-1" style={{ backgroundColor: '#556ee6', borderRadius: '0.375rem', color: 'white' }}>
                            <Label className="mb-0" style={{ color: 'white', fontSize: '11px' }}>Net Total</Label>
                            <h6 className="mb-0" style={{ color: 'white', fontSize: '14px', fontWeight: '600' }}>
                              {formatCurrency(summary.netTotal)}
                            </h6>
                          </div>
                        </Col>
                      </Row>
                    </CardBody>
                  </Card>
                )}

                {/* Closing Balance */}
                {selectedBusiness && selectedBranch && openingBalance && summary && (
                  <Card className="mb-3" style={{ border: '2px solid #dc3545' }}>
                    <CardBody style={{ padding: '0.5rem' }}>
                      <h6 className="mb-1" style={{ color: '#dc3545', fontWeight: 'bold', fontSize: '13px' }}>
                        Closing Balance (Opening + Current)
                      </h6>
                      <Row>
                        <Col md="2">
                          <div className="text-center p-1" style={{ backgroundColor: '#f8f9fa', borderRadius: '0.375rem' }}>
                            <Label className="text-muted mb-0" style={{ fontSize: '11px' }}>Cash Total</Label>
                            <h6 className="mb-0" style={{ fontSize: '14px', fontWeight: '600' }}>{formatCurrency(getClosingBalance.cashTotal)}</h6>
                          </div>
                        </Col>
                        <Col md="2">
                          <div className="text-center p-1" style={{ backgroundColor: '#f8f9fa', borderRadius: '0.375rem' }}>
                            <Label className="text-muted mb-0" style={{ fontSize: '11px' }}>Bank Total</Label>
                            <h6 className="mb-0" style={{ fontSize: '14px', fontWeight: '600' }}>{formatCurrency(getClosingBalance.cashlessTotal)}</h6>
                          </div>
                        </Col>
                        <Col md="2">
                          <div className="text-center p-1" style={{ backgroundColor: '#f8f9fa', borderRadius: '0.375rem' }}>
                            <Label className="text-muted mb-0" style={{ fontSize: '11px' }}>Credit Total</Label>
                            <h6 className="mb-0" style={{ fontSize: '14px', fontWeight: '600' }}>{formatCurrency(getClosingBalance.creditTotal)}</h6>
                          </div>
                        </Col>
                        <Col md="2">
                          <div className="text-center p-1" style={{ backgroundColor: '#f8f9fa', borderRadius: '0.375rem' }}>
                            <Label className="text-muted mb-0" style={{ fontSize: '11px' }}>Returns Total</Label>
                            <h6 className="mb-0" style={{ fontSize: '14px', fontWeight: '600' }}>{formatCurrency(getClosingBalance.returnsTotal)}</h6>
                          </div>
                        </Col>
                        <Col md="4">
                          <div className="text-center p-1" style={{ backgroundColor: '#dc3545', borderRadius: '0.375rem', color: 'white' }}>
                            <Label className="mb-0" style={{ color: 'white', fontSize: '11px' }}>Net Total</Label>
                            <h6 className="mb-0" style={{ color: 'white', fontSize: '14px', fontWeight: '600' }}>
                              {formatCurrency(getClosingBalance.netTotal)}
                            </h6>
                          </div>
                        </Col>
                      </Row>
                    </CardBody>
                  </Card>
                )}
              </CardBody>
            </Card>
          </Col>
        </Row>
      </Container>

      {/* Edit Transaction Modal */}
      <Modal isOpen={showEditModal} toggle={() => setShowEditModal(false)}>
        <ModalHeader toggle={() => setShowEditModal(false)}>
          Edit Transaction Amount
        </ModalHeader>
        <ModalBody>
          {editingTransaction && (
            <Form>
              <FormGroup>
                <Label>Transaction Number</Label>
                <Input value={editingTransaction.transactionNumber} disabled />
              </FormGroup>
              <FormGroup>
                <Label>Net Amount</Label>
                <Input value={formatCurrency(editNetAmount)} disabled />
              </FormGroup>
              <FormGroup>
                <Label>Cash Amount *</Label>
                <Input
                  type="number"
                  step="0.01"
                  value={editCashAmount}
                  onChange={(e) => {
                    const cash = parseFloat(e.target.value) || 0;
                    setEditCashAmount(cash);
                    setEditCashlessAmount(editNetAmount - cash);
                  }}
                />
              </FormGroup>
              <FormGroup>
                <Label>Bank Amount (Cashless) *</Label>
                <Input
                  type="number"
                  step="0.01"
                  value={editCashlessAmount}
                  onChange={(e) => {
                    const cashless = parseFloat(e.target.value) || 0;
                    setEditCashlessAmount(cashless);
                    setEditCashAmount(editNetAmount - cashless);
                  }}
                />
              </FormGroup>
              {editCashAmount + editCashlessAmount !== editNetAmount && (
                <div className="alert alert-warning">
                  Cash + Bank must equal Net Amount ({formatCurrency(editNetAmount)})
                </div>
              )}
              <FormGroup>
                <Label>Notes (Optional)</Label>
                <Input
                  type="textarea"
                  rows="3"
                  value={editNotes}
                  onChange={(e) => setEditNotes(e.target.value)}
                  placeholder="Enter notes for this change..."
                />
              </FormGroup>
            </Form>
          )}
        </ModalBody>
        <ModalFooter>
          <Button color="secondary" onClick={() => setShowEditModal(false)}>
            Cancel
          </Button>
          <Button
            color="primary"
            onClick={handleSaveEdit}
            disabled={editLoading || editCashAmount + editCashlessAmount !== editNetAmount}
          >
            {editLoading ? <ClipLoader size={20} color="#fff" /> : 'Save'}
          </Button>
        </ModalFooter>
      </Modal>

      {/* Day Closing Modal */}
      <Modal isOpen={showDayClosingModal} toggle={() => {
        setShowDayClosingModal(false);
        setClosingDateError("");
        setDayClosingNotes("");
      }}>
        <ModalHeader toggle={() => {
          setShowDayClosingModal(false);
          setClosingDateError("");
          setDayClosingNotes("");
        }}>
          Mark Day Closing
        </ModalHeader>
        <ModalBody>
          <Form>
            <FormGroup>
              <Label>Closing Date <span className="text-danger">*</span></Label>
              <DatePicker
                selected={closingDate}
                onChange={(date) => {
                  setClosingDate(date);
                  setClosingDateError("");
                }}
                dateFormat="dd/MM/yyyy"
                className="form-control"
                maxDate={new Date()}
                placeholderText="Select closing date"
              />
              {closingDateError && (
                <div className="text-danger mt-1" style={{ fontSize: '12px' }}>
                  {closingDateError}
                </div>
              )}
            </FormGroup>
            <FormGroup>
              <Label>Notes (Optional)</Label>
              <Input
                type="textarea"
                rows="3"
                value={dayClosingNotes}
                onChange={(e) => setDayClosingNotes(e.target.value)}
                placeholder="Enter notes for day closing..."
              />
            </FormGroup>
          </Form>
        </ModalBody>
        <ModalFooter>
          <Button color="secondary" onClick={() => {
            setShowDayClosingModal(false);
            setClosingDateError("");
            setDayClosingNotes("");
          }}>
            Cancel
          </Button>
          <Button 
            color="primary" 
            onClick={handleSaveDayClosing}
            disabled={loading || !closingDate}
          >
            {loading ? "Saving..." : "Mark Day Closing"}
          </Button>
        </ModalFooter>
      </Modal>

      {/* Holiday Modal */}
      <Modal isOpen={showHolidayModal} toggle={() => {
        setShowHolidayModal(false);
        setClosingDateError("");
        setHolidayNotes("");
      }}>
        <ModalHeader toggle={() => {
          setShowHolidayModal(false);
          setClosingDateError("");
          setHolidayNotes("");
        }}>
          Mark as Holiday
        </ModalHeader>
        <ModalBody>
          <Form>
            <FormGroup>
              <Label>Holiday Date <span className="text-danger">*</span></Label>
              <DatePicker
                selected={closingDate}
                onChange={(date) => {
                  setClosingDate(date);
                  setClosingDateError("");
                }}
                dateFormat="dd/MM/yyyy"
                className="form-control"
                maxDate={new Date()}
                placeholderText="Select holiday date"
              />
              {closingDateError && (
                <div className="text-danger mt-1" style={{ fontSize: '12px' }}>
                  {closingDateError}
                </div>
              )}
            </FormGroup>
            <FormGroup>
              <Label>Notes (Optional)</Label>
              <Input
                type="textarea"
                rows="3"
                value={holidayNotes}
                onChange={(e) => setHolidayNotes(e.target.value)}
                placeholder="Enter notes for holiday..."
              />
            </FormGroup>
          </Form>
        </ModalBody>
        <ModalFooter>
          <Button color="secondary" onClick={() => {
            setShowHolidayModal(false);
            setClosingDateError("");
            setHolidayNotes("");
          }}>
            Cancel
          </Button>
          <Button 
            color="warning" 
            onClick={handleSaveHoliday}
            disabled={loading || !closingDate}
          >
            {loading ? "Saving..." : "Mark as Holiday"}
          </Button>
        </ModalFooter>
      </Modal>
    </div>
  );
}

